import { DateTime } from 'luxon';
import { translate } from '~/services/i18n';

export const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const moneyFormat = (value = 0): string => {
  const response = new Intl.NumberFormat(translate('APPLICATION.LANG'), {
    currency: translate('APPLICATION.MONEY'),
    style: 'currency',
  }).format(value);

  return response;
};

export const decimalFormat = (value = 0): string => {
  const response = new Intl.NumberFormat(translate('APPLICATION.LANG'), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'decimal',
  }).format(value);

  return response;
};

export const percentFormat = (value = 0): string => {
  const response = new Intl.NumberFormat(translate('APPLICATION.LANG'), {
    style: 'percent',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);

  return response;
};

export const keepOnlyNumbers = (value: string) => {
  return value ? value.replace(/\D+/g, '') : '';
};

export const keepOnlyAlphaNumeric = (value: string) => {
  return value ? value.replace(/[^a-z0-9]/gi, '') : '';
};

export const standardDate = (value: string) => {
  const response = new Intl.DateTimeFormat(translate('APPLICATION.LANG'), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(value));

  return response;
};

export const formatDate = (value: string) => {
  const response = DateTime.fromISO(value).toLocaleString({
    locale: 'pt-br',
  });

  return response;
};

export interface FormatDateMonthOptions {
  originFormat?: string;
}

export const formatDateTo = (value: string, format: string) => {
  if (value === '') {
    return;
  }

  return DateTime.fromISO(value, { zone: 'utc' }).toFormat(format);
};

export const formatDateMonthDay = (value: string) => {
  if (value === '') {
    return;
  }

  return DateTime.fromISO(value, { zone: 'utc' }).toLocaleString({
    month: 'long',
    day: '2-digit',
  });
};

export const formatDateYearMonthDay = (value: string) => {
  if (value === '') {
    return;
  }

  return DateTime.fromISO(value, { zone: 'utc' }).toLocaleString({
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  });
};

export const formatDateWeekDayMonth = (
  value: string,
  straightUtc?: boolean
) => {
  if (!value) {
    return;
  }

  if (straightUtc) {
    return DateTime.fromISO(value, { zone: 'utc' }).toLocaleString({
      month: 'long',
      day: '2-digit',
      weekday: 'long',
    });
  }

  return DateTime.fromISO(value, { zone: 'utc' }).setZone(ZONE).toLocaleString({
    month: 'long',
    day: '2-digit',
    weekday: 'long',
  });
};

export const formatDateWeekDayMonthYear = (
  value: string,
  straightUtc?: boolean
) => {
  if (!value) {
    return;
  }

  if (straightUtc) {
    return DateTime.fromISO(value, { zone: 'utc' }).toLocaleString({
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      weekday: 'long',
    });
  }

  return DateTime.fromISO(value, { zone: 'utc' }).setZone(ZONE).toLocaleString({
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    weekday: 'long',
  });
};

export const formatDateWithHours = (value: string) => {
  if (!value) {
    return;
  }

  return DateTime.fromISO(value).toLocaleString({
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
  });
};

export const formatBilletInput = (value: string) =>
  value.charAt(0) === '8'
    ? value
        .replace(/\D/g, '')
        .replace(/(\d{11})(\d{1})/, '$1-$2')
        .replace(/(\d{1})-(\d{1})(\d{1})/, '$1-$2\n$3')
        .replace(/(\d{11})(\d{1})/, '$1-$2')
        .replace(/(\d{1})-(\d{1})(\d{1})/, '$1-$2\n$3')
        .replace(/(\d{11})(\d{1})/, '$1-$2')
        .replace(/(\d{1})-(\d{1})(\d{1})/, '$1-$2\n$3')
        .replace(/(\d{11})(\d{1})/, '$1-$2')
        .replace(/(\d{1})-(\d{1})(\d{1})$/, '$1-$2\n$3')
    : value
        .replace(/\D/g, '')
        .replace(/(\d{5})(\d{1})/, '$1.$2')
        .replace(/(\d{5})(\d{1})/, '$1\n$2')
        .replace(/(\d{5})(\d{1})/, '$1.$2')
        .replace(/(\d{6})(\d{1})/, '$1\n$2')
        .replace(/(\d{6})\n(\d{5})(\d{1})/, '$1\n$2.$3')
        .replace(/(\d{5}).(\d{6})(\d{1})/, '$1.$2 $3')
        .replace(/ (\d{1})(\d{1})/, ' $1\n$2')
        .replace(/(\d{1})\n(\d{14})$/, ' $1\n$2');

export const formatDateInput = (value: string) =>
  value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d{1})/, '$1/$2')
    .replace(/(\d{2})(\d{1})/, '$1/$2')
    .replace(/(\d{2})\/(\d{2})\/(\d{1})/, '$1/$2/$3')
    .replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$1/$2/$3')
    .replace(/(\/\d{4})\d+?$/, '$1');

export const formatToSqlDate = (value: string) => {
  return DateTime.fromISO(value).toFormat('yyyy-MM-dd');
};

export const standardDateLong = (value: string) =>
  new Intl.DateTimeFormat(translate('APPLICATION.LANG'), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(DateTime.fromISO(value).toJSDate());

export const formatToHourAndMinute = (hour: number, minute = 0) => {
  return DateTime.utc()
    .set({ hour, minute })
    .toFormat(translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON_HOURS'));
};

export const captalizeString = (text: string | undefined) => {
  const ignore = ['DE', 'DA', 'DAS', 'DO', 'DOS', 'E'];

  if (!text) return undefined;

  return text
    .trim()
    .toUpperCase()
    .split(' ')
    .map((word) =>
      ignore.find((v) => word === v)
        ? word.toLowerCase()
        : word[0] + word.substring(1).toLowerCase()
    )
    .join(' ');
};

export const formatCardName = (name) => {
  const names = name.split(' ');
  const NAME_MAX_LENGTH = 19;
  const firstLetters = names.reduce((otherNames, current, index) => {
    if (`${names[0]} ${names[names.length - 1]}`.length < NAME_MAX_LENGTH) {
      return `${names[0]} ${names[names.length - 1]}`;
    }
    if (index === names.length - 1) {
      return `${otherNames} ${current}`;
    }

    return `${otherNames} ${current.substring(0, 1)}`;
  });
  return firstLetters.substring(0, NAME_MAX_LENGTH);
};

export const formatAccountWithDigit = (account: string) => {
  return `${account.slice(0, -1)}-${account.slice(-1)}`;
};

export const removeLeadingZeros = (num: string) => {
  return num.replace(/^0+/, '') || '0';
};

export const formatBilletDueDate = (value: string) => {
  if (!value) {
    return;
  }

  try {
    if (value.includes('/')) {
      const [day, month, year] = value.split('/');
      return `${day}/${month}/${year}`;
    }

    return DateTime.fromISO(value, { zone: 'utc' }).toLocaleString({
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    // Return undefined for invalid dates instead of throwing an error
    return undefined;
  }
};

export const replaceAmpersandWithE = (
  text: string | null | undefined
): string => {
  if (!text) return '';
  return text.replace(/&/g, 'E');
};

export const isValidMoneyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value) && value > 0;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return false;

    const numeric = Number(trimmed);

    return !Number.isNaN(numeric) && numeric > 0;
  }

  return false;
};
